import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';

/* ─── Container that shrinks‐wraps when closed and fills width when open ─── */
// Exported so the research CTA can match the collapsed button exactly
// rather than carrying a second copy of the number.
export const COLLAPSED_HEIGHT = '3.5rem';

const spinGlow = keyframes`
  to {
    --glow-angle: 360deg;
  }
`;

const Container = styled.div`
  ${({ theme }) => theme.mixins.bigButton};
  /* The mixin's padding would offset the collapsed label; the header centres
     itself against COLLAPSED_HEIGHT instead, and Content carries its own. */
  padding: 0;
  overflow: hidden;
  position: relative;

  display: flex;
  flex-direction: column;

  /* Both states are explicit lengths so width actually interpolates. An
     earlier fit-content collapsed width could not animate, which is why the
     morph snapped. collapsedWidth is measured from the label once mounted. */

  width: ${props => (props.open ? '100%' : props.collapsedWidth || 'max-content')};

  max-height: ${props => (props.open ? '70vh' : COLLAPSED_HEIGHT)};

  transition: max-height 0.45s ease-in-out, width 0.45s ease-in-out, transform 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  /* buttonBase centres its label; as a column panel that would shrink-wrap
     and float the children, so stack them from the top at full width. */

  align-items: stretch;
  justify-content: flex-start;
  cursor: ${props => (props.open ? 'default' : 'pointer')};

  /* Collapsed only: a highlight travelling around the border, to draw the eye.
     Painted as the border itself rather than a ring inside it — a transparent
     1px border plus two background layers, a flat fill clipped to the padding
     box and the conic gradient clipped to the border box, so the gradient
     shows only in the border area. Only the gradient's angle animates, so the
     highlight travels without the shape rotating.

     Expanded drops the animation entirely and just holds the accent border:
     the panel is something you read and type into, not something that needs
     to attract attention. */

  ${props =>
    props.open
      ? css`
          border-color: var(--accent);
          background-image: none;
          animation: none;
        `
      : css`
          border-color: transparent;
          background-image: none;

          /* The ring is its own masked pseudo-element now, instead of a conic
             gradient with an opaque fill parked over the middle of it. The
             mask keeps the gradient in the 1px band and cuts the centre out
             entirely, so the button's interior is actually transparent and it
             sits on the page like every other outlined button. */

          &::before {
            content: '';
            position: absolute;
            inset: 0;
            z-index: 0;
            border-radius: inherit;
            padding: 1px;
            background: conic-gradient(
              from var(--glow-angle),
              transparent 0deg,
              transparent 160deg,
              var(--accent-tint) 230deg,
              var(--accent) 300deg,
              var(--accent-tint) 340deg,
              transparent 360deg
            );
            -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
            -webkit-mask-composite: xor;
            mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
            mask-composite: exclude;
            pointer-events: none;
            opacity: 1;
            transition: opacity 0.2s var(--easing);
            animation: ${spinGlow} 5s linear infinite;
          }

          /* Hover hands the border over to a solid accent, and the two cannot
             both be right: the ring keeps travelling a pixel inside the new
             border, which reads as a loose highlight rattling around inside
             the outline. The ring belongs to the resting state, so it fades
             out and lets the hover border do the work. */
          &:hover::before,
          &:focus-visible::before {
            opacity: 0;
          }

          @media (prefers-reduced-motion: reduce) {
            border-color: var(--border-strong);

            &::before {
              display: none;
            }
          }
        `}

  /* The displacement hover belongs to the collapsed button, not to an open
     panel that the visitor is typing into. */
  ${props =>
    props.open &&
    css`
      &:hover,
      &:focus-visible {
        transform: none;
        box-shadow: none;
      }
    `}
`;

/* ─── Header that toggles open/close ────────────────────────────────────── */
const Header = styled.div`
  position: relative;
  /* Above the ring pseudo-element, so the label is never drawn under it. */
  z-index: 1;
  flex: 0 0 ${COLLAPSED_HEIGHT};
  height: ${COLLAPSED_HEIGHT};
  width: 100%;
  font-family: var(--font-sans);
  font-size: var(--fz-lg);
  font-weight: 500;
  color: var(--text-emphasis);
  cursor: pointer;
`;

/* Both labels are stacked and cross-faded rather than swapped, so neither
   pops in or out. They share the same left inset and the same fixed height,
   which is what keeps the text from moving between states. */
const Label = styled.span`
  position: absolute;
  left: var(--space-5);
  top: 0;
  display: flex;
  align-items: center;
  height: 100%;
  /* Fixed height, so a top-only inset shifts the centred label by half its
     value — 0.34em here yields the same 0.17em optical drop as the buttons. */
  padding-top: 0.34em;
  white-space: nowrap;
  opacity: ${props => (props.visible ? 1 : 0)};
  transform: translateY(${props => (props.visible ? '0' : '-4px')});
  pointer-events: none;
  transition: opacity 0.28s var(--easing) ${props => (props.visible ? '0.12s' : '0s')},
    transform 0.28s var(--easing) ${props => (props.visible ? '0.12s' : '0s')};
`;

/* ─── The hidden‑until‑open chat area ──────────────────────────────────── */
const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 var(--space-5) var(--space-5);
  /* buttonBase centres its label; transcript text must not inherit that. */
  text-align: left;
  cursor: default;
  width: 100%;
  opacity: ${props => (props.open ? 1 : 0)};
  visibility: ${props => (props.open ? 'visible' : 'hidden')};

  max-height: ${p => (p.open ? `calc(70vh - ${COLLAPSED_HEIGHT})` : '0')};
  overflow: hidden;
  transition: max-height 0.45s ease-in-out, opacity 0.3s ease ${p => (p.open ? '0.2s' : '0s')};
`;

/* ─── Message list ───────────────────────────────────────────────────── */
const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-top: var(--space-2);
  padding-right: var(--space-1);
  font-size: var(--fz-md);
  line-height: var(--lh-body);

  /* The visitor's turn is a right-aligned bubble; the reply is plain text on
     the panel background. That asymmetry is what makes a transcript readable
     without avatars or name labels. */

  .user {
    display: flex;
    justify-content: flex-end;
    margin-bottom: var(--space-5);

    p {
      max-width: 85%;
      margin: 0;
      padding: calc(var(--space-2) + var(--optical)) var(--space-4)
        calc(var(--space-2) - var(--optical));
      background-color: var(--surface-raised);
      border-radius: 14px;
      color: var(--text);
      word-wrap: break-word;
    }
  }

  .bot {
    margin-bottom: var(--space-5);
    margin-left: 0.3rem;
    color: var(--text);

    /* Markdown elements styling. color:inherit is load-bearing — the panel
       renders inside the hero, whose paragraph rule sets --text-body and
       would otherwise beat the colour inherited from .bot. */
    p {
      margin: 0 0 0.75rem;
      color: inherit;
      word-wrap: break-word;
    }

    /* No overrides: the shared mixin already gives the accent colour and the
       same 0.37em underline offset used by links in body copy. The previous
       white-on-0.2em override sat lower than every other link on the site. */

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    img {
      max-width: 100%;
      border-radius: var(--border-radius);
      margin: 0.5rem 0;
    }

    code {
      background: var(--border);
      padding: 0.2em 0.4em;
      border-radius: 3px;
      font-family: var(--font-sans);
      font-size: 0.9em;
    }

    pre {
      background: var(--border);
      padding: 1rem;
      border-radius: var(--border-radius);
      overflow-x: auto;

      code {
        background: transparent;
        padding: 0;
      }
    }

    ul,
    ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    strong {
      font-weight: 600;
      color: var(--text-emphasis);
    }

    em {
      font-style: italic;
    }
  }
`;

/* One composer box holding the field and the send control, so it reads as a
   single input rather than a textarea with a button bolted beside it. */
const SEND_SIZE = '34px';

const Composer = styled.form`
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-4);
  /* Uniform inset on all four sides. Because the field's single-line height
     equals the send button's, the button sits with the same 8px above it as
     beside and below it — no nudging required. When the field grows the
     button stays anchored to the bottom, which is the expected behaviour. */
  padding: var(--space-2);
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  transition: border-color 0.2s var(--easing);

  &:focus-within {
    border-color: var(--border-strong);
  }
`;

const TextArea = styled.textarea`
  flex: 1;
  resize: none;
  overflow-y: auto;
  max-height: 8rem;
  background: transparent;
  color: var(--text);
  border: 0;
  /* The caret spans the line box while the cap-height band sits a fixed
     ~0.18em above that box's centre — a constant that no line-height or
     padding can remove, so the two cannot both be exactly centred. Tightening
     the line box shortens the caret, and applying half the optical offset
     splits the remaining error between them: each ends up ~1.5px out instead
     of one of them being ~3px out. 20px line box + 14px padding = 34px. */
  min-height: ${SEND_SIZE};
  padding: calc(7px + var(--optical) / 2) var(--space-2) calc(7px - var(--optical) / 2);
  font-family: var(--font-sans);
  font-size: var(--fz-md);
  line-height: var(--lh-heading);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
  }
`;

const SendBtn = styled.button`
  ${({ theme }) => theme.mixins.flexCenter};
  flex: 0 0 auto;
  width: ${SEND_SIZE};
  height: ${SEND_SIZE};
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: var(--accent);
  color: var(--bg);
  transition: var(--transition);

  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2.5;
    fill: none;
  }

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background-color: var(--accent-hover);
  }

  &:disabled {
    background-color: var(--border);
    color: var(--text-disabled);
    cursor: not-allowed;
  }
`;

const ArrowUp = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </svg>
);

const blink = keyframes`
  0%, 80%, 100% { opacity: 0; }
  40%           { opacity: 1; }
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  margin-top: 1.5rem;

  .dot {
    width: 0.5rem;
    height: 0.5rem;
    background: var(--text-muted);
    border-radius: 50%;
    animation: ${blink} 1.4s infinite both;
  }
  .dot:nth-child(1) {
    animation-delay: 0s;
  }
  .dot:nth-child(2) {
    animation-delay: 0.2s;
  }
  .dot:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

/* ─── Component logic ───────────────────────────────────────────────── */
/* Link rendering for bot messages.
   ------------------------------------------------------------------------
   Defined here rather than inline in the components map, because an inline
   arrow is a new component identity on every render, which makes React unmount
   and remount every link in the transcript whenever anything in the panel
   changes. `node` is react-markdown's AST entry and is destructured only to
   keep it off the DOM element. */
// eslint-disable-next-line no-unused-vars
const MarkdownLink = ({ node, href, children, ...props }) => {
  const target = href || '';
  // Anchors and site-relative paths stay in this tab; anything else, and any
  // PDF, opens in a new one.
  const isInternal = target.startsWith('#') || target.startsWith('/');
  const openInNewTab = !isInternal || target.endsWith('.pdf');

  return openInNewTab ? (
    <a href={target} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ) : (
    <a href={target} {...props}>
      {children}
    </a>
  );
};

MarkdownLink.propTypes = {
  node: PropTypes.object,
  href: PropTypes.string,
  children: PropTypes.node,
};

const ChatPanel = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: 'bot',
      text: 'Hi, I am Rain\u2019s AI clone. Ask me about his research, his work, or anything he has written.',
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesRef = useRef(null);
  const textAreaRef = useRef(null);
  const headerRef = useRef(null);
  const containerRef = useRef(null);
  const [collapsedWidth, setCollapsedWidth] = useState(null);
  const openRef = useRef(false);

  // The collapsed width has to be a concrete length for the open/close morph
  // to interpolate. Measure the label text itself rather than the header box:
  // the header is width:100% of the container, so reading its own width would
  // just echo back whatever width we last set. Re-measured once webfonts
  // settle, because Calibre is wider than the fallback.
  useLayoutEffect(() => {
    const measure = () => {
      const el = headerRef.current;
      const container = containerRef.current;
      if (!el || !container || openRef.current) {
        return;
      }
      const range = document.createRange();
      range.selectNodeContents(el);
      const textWidth = range.getBoundingClientRect().width;

      // The label is absolutely positioned, so its inset comes from the token
      // rather than from padding. Reading `left` off it is unreliable: during
      // the layout effect it can still compute to `auto`.
      const inset =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--space-5')) || 24;
      const cs = getComputedStyle(container);
      const borders = parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);

      setCollapsedWidth(`${Math.ceil(textWidth + inset * 2 + borders)}px`);
    };
    measure();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Grow the field with its content up to the CSS max-height, then scroll.
  useEffect(() => {
    const el = textAreaRef.current;
    if (!el || !open) {
      return;
    }
    // With no input there is nothing to grow to, so clear the inline height and
    // let the CSS min-height hold the single-line size. Measuring here instead
    // would read scrollHeight while the panel is still mid-morph at its
    // collapsed width, where the placeholder wraps and inflates the field.
    if (!input) {
      el.style.height = '';
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input, open]);

  // scroll *inside* Messages whenever msg list grows
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTo({
        top: messagesRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const hasUserSpoken = messages.some(m => m.from === 'user');

  const toggle = () => setOpen(o => !o);

  const sendMessage = async e => {
    e.preventDefault();
    const text = input.trim();
    if (!text || isLoading) {
      return;
    }

    setMessages(m => [...m, { from: 'user', text }]);
    setInput('');
    setIsLoading(true);

    const chatMessages = [
      ...messages.map(m => ({
        role: m.from === 'user' ? 'user' : 'assistant',
        content: m.text,
      })),
      { role: 'user', content: text },
    ];

    try {
      const res = await fetch('https://api.rainchong.com/api/chat/stream', {
        //const res = await fetch('http://127.0.0.1:8000/api/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: chatMessages }),
      });

      setMessages(m => [...m, { from: 'bot', text: '' }]);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let firstChunk = true;
      let done = false;
      // let buffer = '{"type": "text", "content": "![10](/me.jpg)\\n- hihi\\n- hoho\\n"}\n\n';
      let buffer = '';

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value) {
          buffer += decoder.decode(value, { stream: true });

          // Split by newlines and process complete JSON objects
          const lines = buffer.split('\n');
          // Keep the last (potentially incomplete) line in the buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            let trimmed = line.trim();
            if (!trimmed) {
              continue;
            }

            // Handle SSE format: strip "data: " prefix if present
            if (trimmed.startsWith('data: ')) {
              trimmed = trimmed.slice(6);
            }
            if (!trimmed) {
              continue;
            }

            try {
              const json = JSON.parse(trimmed);

              // on the very first chunk, stop showing "thinking..."
              if (firstChunk) {
                setIsLoading(false);
                firstChunk = false;
              }

              // append to the last message
              if (json.type === 'text') {
                setMessages(prev => {
                  const next = [...prev];
                  next[next.length - 1].text += json.content;
                  return next;
                });
              } else if (json.type === 'link') {
                // TODO
              }
            } catch (parseError) {
              console.warn('Failed to parse JSON line:', trimmed, parseError);
            }
          }
        }
      }

      // Process any remaining data in buffer after stream ends
      if (buffer.trim()) {
        let remaining = buffer.trim();
        // Handle SSE format: strip "data: " prefix if present
        if (remaining.startsWith('data: ')) {
          remaining = remaining.slice(6);
        }
        try {
          const json = JSON.parse(remaining);
          if (json.type === 'text') {
            setMessages(prev => {
              const next = [...prev];
              next[next.length - 1].text += json.content;
              return next;
            });
          }
        } catch (parseError) {
          console.warn('Failed to parse final buffer:', buffer, parseError);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(m => [...m, { from: 'bot', text: '[ service not available ]' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  return (
    <Container ref={containerRef} open={open} collapsedWidth={collapsedWidth} onClick={toggle}>
      <Header open={open}>
        <Label ref={headerRef} visible={!open}>
          Chat with My AI Clone (obsolete)
        </Label>
        <Label visible={open}>× Close Chat</Label>
      </Header>

      <Content open={open} onClick={e => e.stopPropagation()}>
        <Messages open={open} ref={messagesRef}>
          {messages.map((m, i) => (
            <div key={i} className={m.from}>
              {m.from === 'bot' ? (
                <ReactMarkdown components={{ a: MarkdownLink }}>{m.text}</ReactMarkdown>
              ) : (
                <p>{m.text}</p>
              )}
            </div>
          ))}
          {isLoading && (
            <LoadingIndicator>
              <div className="dot" />
              <div className="dot" />
              <div className="dot" />
            </LoadingIndicator>
          )}
        </Messages>

        <Composer onSubmit={sendMessage} onClick={e => e.stopPropagation()}>
          <TextArea
            ref={textAreaRef}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onClick={e => e.stopPropagation()}
            placeholder={hasUserSpoken ? 'Reply…' : 'Show me your resume'}
          />
          <SendBtn
            type="submit"
            aria-label="Send message"
            disabled={!input.trim() || isLoading}
            onClick={e => e.stopPropagation()}>
            <ArrowUp />
          </SendBtn>
        </Composer>
      </Content>
    </Container>
  );
};

export default ChatPanel;
